/**
 * Build-time feature flags (spec §7/D6, §8.1; issue #56).
 *
 * The AI coach surfaces — Coach AI chat (`CompassCoachAI.jsx`), AI Coach
 * Insights (`AiCoachInsights.jsx`), and the Development plan's "AI
 * Development Plan" tab — carry per-token LLM cost and are off by default
 * until a budget decision is made (spec §7). Off means: absent from nav,
 * their routes redirect rather than render, no disabled buttons or dead
 * ends. The code itself is never deleted — it returns the moment the AI
 * budget decision lands, by flipping this one build-time env var.
 *
 * Set `REACT_APP_ENABLE_AI_COACH=true` at build time (CRA/craco embeds
 * `REACT_APP_*` vars via webpack `DefinePlugin` at build time, same as
 * `REACT_APP_BACKEND_URL` in `lib/api.js`) to turn these surfaces back on.
 * Any other value, or the var being unset entirely, keeps them off — this
 * fails closed, the same default-off polarity `routes/demo.ts`
 * `DEMO_ENABLED` uses on the API side.
 */
export const AI_COACH_ENABLED = process.env.REACT_APP_ENABLE_AI_COACH === "true";

/**
 * The runtime, per-screen "new UI" flag (issue #751, part of epic #749).
 *
 * Every wave of the UX refactor replaces one screen, in the same app,
 * behind `newUi(screen)` — the app is built once in CI and the same bytes
 * are deployed everywhere (`.github/workflows/ci.yml`, `deploy-dev.yml`,
 * `deploy-production.yml` all ship one `frontend-build-<sha>` artifact), so
 * a build-time-only flag cannot give dev a dark new UI and production a live
 * old one at the same time. This flag is checked at RUNTIME instead, from
 * two unioned sources:
 *
 *   - `REACT_APP_NEW_UI_SCREENS` — a build-time, comma-separated allowlist.
 *     Set in NO workflow and NO `wrangler.toml` today (Wave 0 ships no API
 *     endpoint and no Pages env var for this — see issue #751's "explicitly
 *     out of scope"; a guard test in `featureFlags.test.js` pins that no
 *     `.github/workflows/*.yml` sets it). Left unset, this contributes
 *     nothing — but see the ABSOLUTE opt-out below, which does not depend
 *     on this staying unset.
 *   - `localStorage.nw_new_ui` — a runtime, per-browser, comma-separated
 *     allowlist a tester or a Playwright `addInitScript` sets. Same
 *     runtime-client-preference precedent as `nw_text_size` /
 *     `nw_high_contrast` (`frontend/public/index.html`).
 *
 * **Unset means ON (D202); a stored "none" means off, ABSOLUTELY.** No
 * `localStorage` key and no env var ⇒ `newUi(anything) === true` for every
 * screen in `NEW_UI_SCREENS` (chained screens resolve through their own
 * dependency too, since `"all"` is what an unset default now means) —
 * Andreas's own overlay ruling D202, 2026-09-30 ("flick the switch"; issue
 * #999): there are no real users yet, so the redrawn screens are the
 * default everyone gets, not an opt-in.
 *
 * `localStorage.nw_new_ui = "none"` (trimmed, case-insensitive) opts back
 * OUT to the old UI everywhere, and wins over EVERYTHING else — including a
 * build-time list, should `REACT_APP_NEW_UI_SCREENS` ever be set (review
 * round 2, #999's own F1). This is deliberately NOT symmetric with a
 * build-time `"none"`, which only silences the BUILD source's own
 * contribution and leaves `localStorage` free to turn screens back on:
 * `localStorage` is the one source a human or a test genuinely controls at
 * runtime — the e2e flag-off leg (`frontend/e2e/newUiFlag.js`) writes
 * exactly `nw_new_ui = "none"` for every context, and that leg must stay on
 * the old UI no matter what a future build sets `REACT_APP_NEW_UI_SCREENS`
 * to. `"all"` in either source turns every screen on explicitly; a comma
 * list turns on exactly the screens named, same as always (a stored value
 * only counts as the absolute opt-out when, trimmed and lowercased, it is
 * the bare string `"none"` — `"none,record"` is an ordinary comma list, and
 * behaves as documented above: `"record"` on, everything else off, exactly
 * as if `"none"` had never appeared in it).
 *
 * The `localStorage` read is wrapped in try/catch — a private window, or
 * storage blocked by policy, throws on access, and that must fall back to
 * the build-time list, then to the unset-means-ON default, rather than
 * take the app down.
 *
 * Read fresh on every call, deliberately never cached in a module-level
 * constant: a tester who runs `localStorage.setItem("nw_new_ui", "all")` in
 * the console and reloads, and a Playwright `context.addInitScript` that
 * sets it before the first page script runs, both need the NEXT call to see
 * the new value — a value captured once at module load would miss both.
 */

/**
 * The screen ids every later wave uses, fixed here once as the contract —
 * a wave that adds a screen adds its id to this one array, and every
 * `newUi(...)` call site elsewhere quotes one of these strings.
 */
export const NEW_UI_SCREENS = [
  "signup",
  "setup",
  "coach-setup",
  "coach-portal-empty",
  "log-ground",
  "log-scorecard",
  "log-saved",
  "record",
  "season-record",
  "plan",
  "plan-milestones",
  // Issue #940, D175 P2 — the `/development` chrome slice (navy TopBar,
  // D92's DestinationRow, the "Your plan" header, the sentence-case tab
  // strip). Depends on `plan` (below, same dependency shape D166 M12 gives
  // `plan-milestones` just above) — an orchestrator ruling, not a
  // technical necessity this file discovers on its own: D175 P2 asks for
  // it explicitly, so `newUi("dev-chrome")` alone (no `plan`) stays
  // exactly today's flag-off behaviour. #876 later chains `reflections` to
  // this same flag.
  "dev-chrome",
  "grounds",
  "ground-card",
  "squad",
  // Issue #973 (D186.3) — the coach's one-player page, `CoachPlayer.dc.html`.
  // Depends on `squad` (the same dependency shape D149/D166 M12/D175 P2 give
  // `season-record`/`plan-milestones`/`dev-chrome` above): a coach reaches
  // this page from a squad row tap, so shipping it without the squad
  // rebuilt would open a redrawn player page from a shipped roster row that
  // never navigates there.
  "coach-player",
  // Issue #874, slice S1 — the front door (`/`) and sign-in (`/login`),
  // redrawn to FrontDoor.dc.html/SignIn.dc.html (design-baseline-round-5).
  // Presentation only: every auth handler (password, passkey, remember-
  // device, the sign-in-link door, session-ended, the demo entry) is reused
  // exactly, via the same AuthContext calls the shipped screens make.
  "signin",
  // Issue #852 (epic #847, voice wave V1) — the Reflections tab rebuilt to
  // ReflectVoice.dc.html/Reflect.dc.html/ReflectMore.dc.html, without a
  // microphone yet (that lands on top of this in #853).
  "reflections",
  // Issue #974 (D186.12) — the interim privacy notice (/privacy) redrawn
  // to Privacy.dc.html. Its own flag, with NO chain: unlike
  // `plan-milestones`/`dev-chrome`/`reflections` above, `newUi("privacy")`
  // is not special-cased below and answers on its raw membership alone.
  "privacy",
  // Issue #975 (D186.8) — Settings, slice A: the page, the account &
  // profile card and coach access, redrawn to Settings.dc.html. Its own
  // flag, with NO chain, the same shape `newUi("privacy")` above has:
  // Settings B (the opened folds and the coach variant) lands behind this
  // SAME flag rather than a chained second one (D186.8 — "one flag for
  // both"), so no special case is added below for it either.
  "settings",
  // Issue #1030 (D107, D130, #726) — the phone match list on `/performance`,
  // redrawn to MatchListFold.dc.html: every card folded to opponent, date,
  // Runs, Runs conceded and Wkts, one tap to open the rest. Its own flag,
  // with NO chain, the same shape `newUi("privacy")`/`newUi("settings")`
  // above have: `newUi("matches")` is not special-cased below and answers
  // on its raw membership alone. Player role only (the page's own router
  // guard also admits admin, who keeps the shipped page).
  "matches",
  // Issue #1316 (Pick XI coach 1 of 3; D297, D302, D309) — Team selection,
  // Your games and Schedule a game (`/coach-portal/team`, `/games`,
  // `/games/schedule`), coach only. Its own flag, with NO chain: the coach's
  // entry lives inside `CoachSquad` (already `squad`) and the player's inside
  // Plan (already `plan`). D202 makes the id ON when nothing is stored, so the
  // flag alone hides nothing. #1318 turns the portal's Team selection
  // on behind it: with the flag on it is a link to /coach-portal/team, with it
  // off the disabled button and its sentence stay (D217).
  "pick-xi",
  // Issue #1514 (D406, D407, D407a) — the guided tour (`/tour`, `/tour/coach`),
  // shown once after player onboarding or coach set-up and replayable from
  // Settings > More. Its own flag, with NO chain: `newUi("tour")` answers on
  // its raw membership alone. With it off, onboarding and coach set-up
  // navigate exactly as before and Settings draws no "Take the tour" row (D217).
  "tour",
  // Issue #1036 (Payments 1; D204.2, D415) — the three pay steps (`/plan`,
  // `/plan/return`, `/plan/failed`), on Stripe test or live keys. Its own flag, with NO
  // chain. The pages have no legacy version, so with it off each route
  // redirects away and renders nothing; the access gate (`BILLING_ENFORCED`)
  // stays off and is a separate decision.
  "pay",
];

/**
 * The screens the new UI is on for, as a set, read fresh from both sources.
 *
 * D202 (issue #999) — when NEITHER source is set at all (no
 * `REACT_APP_NEW_UI_SCREENS` and no `localStorage.nw_new_ui` key), the
 * default is now `{"all"}`, not the empty set.
 *
 * Review round 2, F1 — a STORED `"none"` (trimmed, case-insensitive) is an
 * ABSOLUTE opt-out: it returns the empty set regardless of what the
 * build-time source says, including a real screen list. Without this, a
 * future build that ever set `REACT_APP_NEW_UI_SCREENS` would silently turn
 * the e2e flag-off leg (which writes only `nw_new_ui = "none"`, never
 * touching the build var) into a flag-ON leg — exactly the kind of failure
 * this whole issue exists to close for the app default itself.
 *
 * A build-time `"none"` is NOT absolute the same way — it only silences the
 * build source's own contribution, so a stored real list still applies on
 * top of it (same shape a build-time `"none"` had before this round: "that
 * source contributes nothing"). Any other value parses exactly as always: a
 * comma-separated allowlist, unioned across both sources (a stored value
 * that merely CONTAINS "none" alongside other names, e.g. `"none,record"`,
 * is an ordinary comma list, not the absolute opt-out — "none" is simply an
 * id nothing is ever behind, same as any other typo would be).
 */
function newUiScreens() {
  const build = process.env.REACT_APP_NEW_UI_SCREENS || "";
  const buildIsSet = build.trim() !== "";
  const buildIsNone = build.trim().toLowerCase() === "none";

  let stored = "";
  let storedIsSet = false;
  try {
    const raw = window.localStorage.getItem("nw_new_ui");
    if (raw !== null && raw.trim() !== "") {
      stored = raw;
      storedIsSet = true;
    }
  } catch {
    // Private window, or storage blocked by policy: fall through to the
    // build-time list only. Never let a throwing localStorage take the
    // flag check down with it.
  }
  const storedIsNone = storedIsSet && stored.trim().toLowerCase() === "none";

  if (storedIsNone) {
    // ABSOLUTE opt-out (review round 2, F1): wins over the build-time
    // source too, not merely over an unset default.
    return new Set();
  }

  if (!buildIsSet && !storedIsSet) {
    // Unset means ON (D202): nothing said either way defaults to every
    // screen, the production default now that there are no real users yet.
    return new Set(["all"]);
  }

  const out = new Set();
  if (buildIsSet && !buildIsNone) {
    for (const s of build.split(",")) if (s.trim()) out.add(s.trim());
  }
  if (storedIsSet) {
    for (const s of stored.split(",")) if (s.trim()) out.add(s.trim());
  }
  return out;
}

/**
 * Is `screen` on the new UI? Unset (no env var, no localStorage key) is
 * always true, for every screen in `NEW_UI_SCREENS` (D202, issue #999) —
 * `localStorage.nw_new_ui = "none"` opts back out to the old UI.
 *
 * CALL SITE CONTRACT (issue #751, R7 of the 2026-09-20 review): always pass
 * a string literal drawn from `NEW_UI_SCREENS`, never a variable or a
 * computed expression. `frontend/src/lib/newUiScreenContract.test.js`
 * scans every call in `frontend/src` and fails on a literal that is not in
 * `NEW_UI_SCREENS` (a typo like `newUi("recrod")` would otherwise fail
 * CLOSED and SILENTLY — both e2e legs stay green and identical forever,
 * because nothing is ever behind the misspelled name to disagree) — and it
 * refuses a non-literal argument outright, because that scan cannot verify
 * one. No call site needs a computed screen id today; if one ever does,
 * that is a decision to revisit this rule deliberately, not a gap to slip
 * a dynamic call through unexamined.
 */
/**
 * D149 (issue #762, PR #880 review, Andreas) — `season-record` turns on
 * only when `record` is ALSO on. Encoded HERE, inside `newUi` itself
 * (Andreas's own words: "Gate it in computeSeasonRecordGate/newUi"),
 * rather than by changing every call site's own ternary to a wrapper
 * function: `App.js`'s `/statistics` redirect and `flagSafeLinks.test.js`'s
 * own gate-shape derivation (`renderedTagsViaGateShapeFromCode` and
 * friends) pattern-match the LITERAL text `newUi("...")  ? <Tag/> : …` —
 * swapping that call for a same-shaped wrapper (`seasonRecordOn() ? …`)
 * would silently drop `/statistics` out of that file's own
 * `EXPECTED_GATED_ROUTES`/`SCANNED_FILES` derivation, the exact "a route
 * quietly stops being checked" failure mode that whole file exists to
 * catch. Every existing `newUi("season-record")` call site (App.js,
 * `Layout.jsx`, `Dashboard.jsx`) stays completely unchanged and gets the
 * combined answer automatically.
 *
 * WHY THE DEPENDENCY: the season record absorbs "My Stats" (Decision 11)
 * and replaces the populated dashboard for a 2+-match account, but only
 * #760/#761's OWN screens (`record`) establish the redrawn record
 * identity at 0/1 matches in the first place — shipping the season screen
 * without them would let a 2-match account see the new record while a 0-
 * or 1-match account on the SAME build still saw the shipped dashboard
 * underneath it, two different "records" stitched together by match count
 * alone.
 *
 * `computeSeasonRecordGate` (`lib/recordGate.js`) ALSO takes an explicit
 * `recordScreenOn` param and ANDs it in independently — belt and braces,
 * not redundant dead code: that function is a plain, React-free unit
 * (this repo's own testability convention), so it cannot call `newUi`
 * itself, and its own test pins the AND directly without needing this
 * module at all.
 */
/**
 * D166 M12 (issue #764's own orchestrator ruling, decisions-2026-09-24.md)
 * — `plan-milestones` turns on only when `plan` is ALSO on, the exact same
 * dependency shape D149 gave `season-record`/`record` above (see that
 * block's own comment for why the dependency lives HERE rather than at
 * each call site): the milestones step is a per-goal drill-down FROM the
 * redrawn `/development` page #763 built, so shipping it without that page
 * would give a player a step that opens from nowhere.
 */
/**
 * D175 P2 (issue #940/#957, #876's own follow-up comment) — `reflections`
 * turns on only when `dev-chrome` is ALSO on, the exact same dependency
 * shape D166 M12 gives `plan-milestones`/`plan` above: the rebuilt
 * Reflections tab body (#852) is drawn to sit inside `/development`'s new
 * chrome (`DevelopmentChrome.jsx`, #940), and shipping the body without
 * that chrome around it is the "19 chrome reds" #876 was parked on.
 */
// F1 (#876 review, 2026-09-30) — `dev-chrome`'s OWN resolved value
// (`on && plan`), factored out so `reflections` (below) can reuse it
// directly instead of re-deriving it from the raw set. A raw
// `set.has("dev-chrome")` at the `reflections` call site missed that
// `dev-chrome` itself requires `plan`: `nw_new_ui =
// "reflections,dev-chrome"` with no `plan` resolved `dev-chrome`'s own
// `newUi("dev-chrome")` to false, but still passed a raw `set.has`
// check, rendering the rebuilt Reflections body inside the SHIPPED
// (chrome-less) `/development` page — exactly the state this chain
// exists to prevent. A private helper (not a recursive `newUi(...)`
// call, which `flagSafeLinks.test.js`'s own derivation would otherwise
// mistake for a new route-gating entry point inside this definition
// file itself) means the two can never drift apart again.
function devChromeResolved(set) {
  return (set.has("all") || set.has("dev-chrome")) && (set.has("all") || set.has("plan"));
}

export function newUi(screen) {
  const set = newUiScreens();
  const on = set.has("all") || set.has(screen);
  if (screen === "season-record") return on && (set.has("all") || set.has("record"));
  if (screen === "plan-milestones") return on && (set.has("all") || set.has("plan"));
  if (screen === "dev-chrome") return devChromeResolved(set);
  if (screen === "reflections") return on && devChromeResolved(set);
  // D186.3 (issue #973, decisions-2026-09-24.md) — `coach-player` turns on
  // only when `squad` is ALSO on, the same dependency shape D149/D166
  // M12/D175 P2 give above.
  if (screen === "coach-player") return on && (set.has("all") || set.has("squad"));
  return on;
}
